import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime, formatRelative } from '../ui/format';
import { Badge, ErrorState, Loading, type BadgeTone } from '../ui/Page';
import {
  listMyTickets,
  rateMyTicket,
  readMyTicket,
  replyToMyTicket,
  type PortalTicket,
} from './api';

/**
 * A customer's own chamados (ADR 0012). Phase 47.
 *
 * No session, no navigation, no product chrome. Somebody who was sent a link should see their
 * chamados, open one, read what was said and answer — and nothing else. The link is the whole
 * credential, so there is no sign-in to offer and no account to create.
 *
 * A link that has expired or been revoked is indistinguishable from one that was never real: the
 * page says the link no longer works and offers no way to probe which.
 */

const TONE: Record<string, BadgeTone> = {
  OPEN: 'info',
  IN_PROGRESS: 'info',
  PENDING_CUSTOMER: 'warning',
  ON_HOLD: 'warning',
  RESOLVED: 'positive',
  AWAITING_REVIEW: 'warning',
  CLOSED: 'neutral',
  CANCELED: 'neutral',
};

export function PortalPage(): React.JSX.Element {
  const { token = '' } = useParams();
  const [open, setOpen] = useState<string | null>(null);

  const tickets = useQuery({
    queryKey: ['portal', token],
    queryFn: () => listMyTickets(token),
    enabled: token !== '',
    retry: false,
  });

  if (tickets.isLoading) return <Loading />;

  if (tickets.error) {
    return (
      <main className="public">
        <h1>{t('portal.gone')}</h1>
        <p className="muted">{t('portal.goneBody')}</p>
      </main>
    );
  }

  const list = tickets.data?.tickets ?? [];

  return (
    <main className="public">
      <header className="public__head">
        <h1>{t('portal.title')}</h1>
        <p className="muted">{tickets.data?.email}</p>
      </header>

      {list.length === 0 ? (
        <p className="muted">{t('portal.none')}</p>
      ) : (
        <ul className="portal__list">
          {list.map((ticket) => (
            <li key={ticket.id}>
              <button
                type="button"
                className={`portal__item${open === ticket.id ? ' is-open' : ''}`}
                aria-expanded={open === ticket.id}
                onClick={() => setOpen(open === ticket.id ? null : ticket.id)}
              >
                <span className="cell-stack">
                  <span className="strong">{`#${ticket.number} · ${ticket.subject}`}</span>
                  <span className="muted small">
                    {t('portal.openedAt').replace('{when}', formatRelative(ticket.createdAt))}
                  </span>
                </span>

                <Badge tone={TONE[ticket.status] ?? 'neutral'}>
                  {t(`support.status.${ticket.status}`)}
                </Badge>
              </button>

              {open === ticket.id && <Thread token={token} ticket={ticket} />}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

function Thread({ token, ticket }: { token: string; ticket: PortalTicket }): React.JSX.Element {
  const client = useQueryClient();
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const thread = useQuery({
    queryKey: ['portal-thread', token, ticket.id],
    queryFn: () => readMyTicket(token, ticket.id),
    retry: false,
  });

  const send = useMutation({
    mutationFn: () => replyToMyTicket(token, ticket.id, body.trim()),
    onSuccess: () => {
      setBody('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['portal-thread', token, ticket.id] });
      void client.invalidateQueries({ queryKey: ['portal', token] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (thread.isLoading) return <Loading />;
  if (thread.error)
    return <ErrorState error={thread.error} onRetry={() => void thread.refetch()} />;

  const messages = thread.data?.messages ?? [];
  const current = thread.data ?? ticket;

  return (
    <div className="portal__thread">
      {current.canRate && <Review token={token} ticket={current} />}

      {messages.length === 0 ? (
        <p className="muted small">{t('portal.noMessages')}</p>
      ) : (
        <ol className="portal__messages">
          {messages.map((message) => (
            <li
              key={message.id}
              className={`portal__message${message.fromCustomer ? ' portal__message--mine' : ''}`}
            >
              <p>{message.body}</p>
              <span className="muted small">{formatDateTime(message.createdAt)}</span>
            </li>
          ))}
        </ol>
      )}

      {/* A finished chamado is history, not a mailbox: the box is absent rather than disabled. */}
      {ticket.open ? (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            if (body.trim()) send.mutate();
          }}
        >
          <textarea
            rows={3}
            value={body}
            maxLength={20_000}
            placeholder={t('portal.replyPlaceholder')}
            onChange={(event) => setBody(event.target.value)}
          />

          {error && (
            <p className="alert small" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="button button--primary button--compact"
            disabled={body.trim() === '' || send.isPending}
          >
            {send.isPending ? t('support.sending') : t('portal.send')}
          </button>
        </form>
      ) : (
        <p className="muted small">{t('portal.finished')}</p>
      )}
    </div>
  );
}

/**
 * The requester's verdict, and the deadline attached to it.
 *
 * A chamado sent for evaluation is cancelled if nobody answers within the window, so the window is
 * stated here rather than left as a surprise: somebody who reads "responda até 8 de março" and does
 * nothing has made a choice, and somebody who was never told has had one made for them.
 *
 * Five stars and an optional sentence. Disagreeing is not done here — it is done by replying below,
 * which reopens the chamado — because a one-star rating is feedback, not a bug report, and pretending
 * otherwise would leave broken things closed with a bad score attached.
 */
function Review({ token, ticket }: { token: string; ticket: PortalTicket }): React.JSX.Element {
  const client = useQueryClient();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => rateMyTicket(token, ticket.id, rating, comment.trim() || null),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['portal-thread', token, ticket.id] });
      void client.invalidateQueries({ queryKey: ['portal', token] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <section className="portal__review">
      <h2 className="small strong">{t('portal.rateTitle')}</h2>
      <p className="muted small">{t('portal.rateBody')}</p>

      {ticket.reviewDueAt !== null && (
        <p className="muted small">
          {t('portal.rateDeadline').replace('{when}', formatDateTime(ticket.reviewDueAt))}
        </p>
      )}

      <div className="portal__stars" role="radiogroup" aria-label={t('portal.rateTitle')}>
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={rating === value}
            aria-label={String(value)}
            className={`chip${rating >= value ? ' chip--active' : ''}`}
            disabled={send.isPending}
            onClick={() => setRating(value)}
          >
            {'★'}
          </button>
        ))}
      </div>

      <textarea
        rows={2}
        value={comment}
        maxLength={2000}
        placeholder={t('portal.ratePlaceholder')}
        onChange={(event) => setComment(event.target.value)}
      />

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}

      <button
        type="button"
        className="button button--primary button--compact"
        disabled={rating === 0 || send.isPending}
        onClick={() => send.mutate()}
      >
        {send.isPending ? t('support.sending') : t('portal.rateSend')}
      </button>
    </section>
  );
}
